import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Card, CardContent, Grid, Tabs, Tab, Divider,
  IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import InfoIcon from '@mui/icons-material/Info';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Lab {
  id: string;
  lab_id: string;
  name: string;
  clia_number: string | null;
  company_name: string | null;
  contact_email: string | null;
  is_active: boolean;
  integration_count: number;
  created_at: string | null;
}

interface Integration {
  id: string;
  lis_system: string;
  integration_type: string;
  config: Record<string, any>;
  is_active: boolean;
  last_connected_at: string | null;
  last_error: string | null;
}

interface MonthlyTrend {
  month: string;
  reports: number;
  api_calls: number;
  total: number;
}

interface LabDetail {
  id: string;
  lab_id: string;
  name: string;
  clia_number: string | null;
  company_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  testing_methodology?: string | null;
  billing_contact_name: string | null;
  billing_email: string | null;
  billing_phone: string | null;
  technical_contact_name: string | null;
  technical_email: string | null;
  technical_phone: string | null;
  is_active: boolean;
  integrations: Integration[];
  usage: Record<string, any>;
  monthly_trend: MonthlyTrend[];
}

const LabManager: React.FC = () => {
  const navigate = useNavigate();
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create/Edit lab dialog
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [editingLab, setEditingLab] = useState<boolean>(false);
  const [labForm, setLabForm] = useState({
    lab_id: '', name: '', clia_number: '', company_name: '',
    contact_email: '', contact_phone: '', testing_methodology: 'molecular',
    billing_contact_name: '', billing_email: '', billing_phone: '',
    technical_contact_name: '', technical_email: '', technical_phone: '',
  });

  // Detail view
  const [selectedLab, setSelectedLab] = useState<LabDetail | null>(null);
  const [detailTab, setDetailTab] = useState(0);

  // Integration form
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [integrationForm, setIntegrationForm] = useState({
    lis_system: '', integration_type: 'sftp',
    host: '', port: '22', username: '', password: '', key_file: '',
    remote_path_in: '', remote_path_out: '',
    callback_url: '', callback_secret: '', callback_events: 'report_ready',
  });

  // Test connection state
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [dialogTestResult, setDialogTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [dialogTesting, setDialogTesting] = useState(false);
  const [keyFileUploading, setKeyFileUploading] = useState(false);
  const [keyFileName, setKeyFileName] = useState<string | null>(null);

  const getHeaders = () => authService.getAuthHeaders();

  const fetchLabs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/lis-portal/labs`, { headers: getHeaders() });
      if (response.data.success) {
        setLabs(response.data.labs || []);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch labs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLabs(); }, []);

  const fetchLabDetail = async (labId: string) => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/lis-portal/labs/${labId}`, { headers: getHeaders() });
      if (response.data.success) {
        setSelectedLab(response.data.lab);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch lab details');
    }
  };

  // ── Lab CRUD ─────────────────────────────────────────────────

  const handleCreateLab = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/v1/lis-portal/labs`, labForm, { headers: getHeaders() });
      if (response.data.success) {
        setLabDialogOpen(false);
        resetLabForm();
        fetchLabs();
      } else {
        setError(response.data.error || 'Failed to create lab');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create lab');
    }
  };

  const handleUpdateLab = async () => {
    if (!selectedLab) return;
    try {
      const updateData: any = {
        name: labForm.name, clia_number: labForm.clia_number, company_name: labForm.company_name,
        contact_email: labForm.contact_email, contact_phone: labForm.contact_phone,
        testing_methodology: labForm.testing_methodology,
      };
      if (labForm.billing_contact_name) updateData.billing_contact_name = labForm.billing_contact_name;
      if (labForm.billing_email) updateData.billing_email = labForm.billing_email;
      if (labForm.billing_phone) updateData.billing_phone = labForm.billing_phone;
      if (labForm.technical_contact_name) updateData.technical_contact_name = labForm.technical_contact_name;
      if (labForm.technical_email) updateData.technical_email = labForm.technical_email;
      if (labForm.technical_phone) updateData.technical_phone = labForm.technical_phone;
      const response = await axios.put(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}`,
        updateData,
        { headers: getHeaders() }
      );
      if (response.data.success) {
        setLabDialogOpen(false);
        setEditingLab(false);
        resetLabForm();
        fetchLabs();
        fetchLabDetail(selectedLab.lab_id);
      } else {
        setError(response.data.error || 'Failed to update lab');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update lab');
    }
  };

  const openEditDialog = () => {
    if (!selectedLab) return;
    setEditingLab(true);
    setLabForm({
      lab_id: selectedLab.lab_id,
      name: selectedLab.name,
      clia_number: selectedLab.clia_number || '',
      company_name: selectedLab.company_name || '',
      contact_email: selectedLab.contact_email || '',
      contact_phone: selectedLab.contact_phone || '',
      testing_methodology: selectedLab.testing_methodology || 'molecular',
      billing_contact_name: selectedLab.billing_contact_name || '',
      billing_email: selectedLab.billing_email || '',
      billing_phone: selectedLab.billing_phone || '',
      technical_contact_name: selectedLab.technical_contact_name || '',
      technical_email: selectedLab.technical_email || '',
      technical_phone: selectedLab.technical_phone || '',
    });
    setLabDialogOpen(true);
  };

  const resetLabForm = () => {
    setLabForm({ lab_id: '', name: '', clia_number: '', company_name: '', contact_email: '', contact_phone: '', testing_methodology: 'molecular', billing_contact_name: '', billing_email: '', billing_phone: '', technical_contact_name: '', technical_email: '', technical_phone: '' });
  };

  // ── Integration CRUD ─────────────────────────────────────────

  const handleCreateIntegration = async () => {
    if (!selectedLab) return;
    const config: Record<string, any> = {};
    if (integrationForm.integration_type === 'sftp') {
      config.host = integrationForm.host;
      config.port = parseInt(integrationForm.port) || 22;
      config.username = integrationForm.username;
      if (integrationForm.password) config.password = integrationForm.password;
      if (integrationForm.key_file) config.key_file = integrationForm.key_file;
      config.remote_path_in = integrationForm.remote_path_in;
      config.remote_path_out = integrationForm.remote_path_out;
    } else if (integrationForm.integration_type === 'webhook') {
      config.callback_url = integrationForm.callback_url;
      if (integrationForm.callback_secret) config.callback_secret = integrationForm.callback_secret;
      config.callback_events = integrationForm.callback_events;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/integrations`,
        { lis_system: integrationForm.lis_system, integration_type: integrationForm.integration_type, config },
        { headers: getHeaders() }
      );
      if (response.data.success) {
        setIntegrationDialogOpen(false);
        resetIntegrationForm();
        fetchLabDetail(selectedLab.lab_id);
      } else {
        setError(response.data.error || 'Failed to create integration');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create integration');
    }
  };

  const handleRequestRemoval = async (integration: Integration) => {
    if (!selectedLab) return;
    if (!window.confirm(`Submit a support ticket to request removal of the "${integration.lis_system}" integration?`)) return;
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/lis-portal/support-tickets`,
        {
          subject: `Request removal of ${integration.lis_system} integration`,
          description: `Requesting removal of ${integration.integration_type.toUpperCase()} integration "${integration.lis_system}" (ID: ${integration.id}) from lab "${selectedLab.name}" (${selectedLab.lab_id}).`,
          category: 'integration_removal',
          priority: 'normal',
          lab_id: selectedLab.lab_id,
        },
        { headers: getHeaders() }
      );
      if (response.data.success) {
        navigate('/lis-portal/support-tickets');
      } else {
        setError(response.data.error || 'Failed to create ticket');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create ticket');
    }
  };

  const handleTestIntegration = async (integrationId: string) => {
    if (!selectedLab) return;
    setTestResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/integrations/${integrationId}/test`,
        {},
        { headers: getHeaders() }
      );
      setTestResult(response.data);
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || err.response?.data?.error || 'Test failed' });
    }
  };

  const handleTestBeforeSave = async () => {
    setDialogTesting(true);
    setDialogTestResult(null);
    const config: Record<string, any> = {};
    if (integrationForm.integration_type === 'sftp') {
      config.host = integrationForm.host;
      config.port = parseInt(integrationForm.port) || 22;
      config.username = integrationForm.username;
      if (integrationForm.password) config.password = integrationForm.password;
      if (integrationForm.key_file) config.key_file = integrationForm.key_file;
    } else if (integrationForm.integration_type === 'webhook') {
      config.callback_url = integrationForm.callback_url;
      if (integrationForm.callback_secret) config.callback_secret = integrationForm.callback_secret;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/test-connection`,
        { lis_system: integrationForm.lis_system || 'test', integration_type: integrationForm.integration_type, config },
        { headers: getHeaders() }
      );
      setDialogTestResult(response.data);
    } catch (err: any) {
      setDialogTestResult({ success: false, message: err.response?.data?.message || err.response?.data?.error || 'Test failed' });
    } finally {
      setDialogTesting(false);
    }
  };

  const resetIntegrationForm = () => {
    setIntegrationForm({
      lis_system: '', integration_type: 'sftp',
      host: '', port: '22', username: '', password: '', key_file: '',
      remote_path_in: '', remote_path_out: '',
      callback_url: '', callback_secret: '', callback_events: 'report_ready',
    });
    setDialogTestResult(null);
    setKeyFileName(null);
  };

  // ── Render ───────────────────────────────────────────────────

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  // Detail view
  if (selectedLab) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Button onClick={() => { setSelectedLab(null); setTestResult(null); }} sx={{ mb: 1 }}>&larr; Back to Labs</Button>
            <Typography variant="h5">{selectedLab.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Lab ID: {selectedLab.lab_id}
              {selectedLab.clia_number && ` | CLIA: ${selectedLab.clia_number}`}
              {selectedLab.company_name && ` | ${selectedLab.company_name}`}
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={openEditDialog}>
            Edit Lab
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 3 }}>
          <Tab icon={<SettingsInputComponentIcon />} label="Integrations" />
          <Tab icon={<BarChartIcon />} label="Usage" />
          <Tab icon={<InfoIcon />} label="Details" />
        </Tabs>

        {/* Integrations Tab */}
        {detailTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">LIS Integrations</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetIntegrationForm(); setIntegrationDialogOpen(true); }}>
                Add Integration
              </Button>
            </Box>

            {testResult && (
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setTestResult(null)}>
                {testResult.message}
              </Alert>
            )}

            {selectedLab.integrations.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No integrations configured yet. Add one to start receiving reports.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {selectedLab.integrations.map((i) => (
                  <Grid item xs={12} md={6} key={i.id}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6">{i.lis_system}</Typography>
                          <Box>
                            <Chip label={i.integration_type.toUpperCase()} size="small" color="primary" sx={{ mr: 1 }} />
                            <Chip label={i.is_active ? 'Active' : 'Inactive'} size="small"
                              color={i.is_active ? 'success' : 'default'} />
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        {Object.entries(i.config).map(([k, v]) => (
                          <Typography key={k} variant="body2" sx={{ mb: 0.5 }}>
                            <strong>{k}:</strong> {String(v)}
                          </Typography>
                        ))}
                        {i.last_connected_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Last connected: {new Date(i.last_connected_at).toLocaleString()}
                          </Typography>
                        )}
                        {i.last_error && (
                          <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{i.last_error}</Alert>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                          <Button size="small" variant="outlined" color="primary" startIcon={<PlayArrowIcon />}
                            onClick={() => handleTestIntegration(i.id)}>
                            Test Connection
                          </Button>
                          <Tooltip title="Submit a support ticket to request removal">
                            <Button size="small" variant="outlined" color="warning" startIcon={<SupportAgentIcon />}
                              onClick={() => handleRequestRemoval(i)}>
                              Request Removal
                            </Button>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Usage Tab */}
        {detailTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Monthly Usage</Typography>
            {selectedLab.monthly_trend.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No usage data yet.</Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Reports</TableCell>
                      <TableCell align="right">API Calls</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedLab.monthly_trend.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell>{m.month}</TableCell>
                        <TableCell align="right">{m.reports.toLocaleString()}</TableCell>
                        <TableCell align="right">{m.api_calls.toLocaleString()}</TableCell>
                        <TableCell align="right"><strong>{m.total.toLocaleString()}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Details Tab */}
        {detailTab === 2 && (
          <Box>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>General</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="body2"><strong>Company:</strong> {selectedLab.company_name || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2"><strong>CLIA:</strong> {selectedLab.clia_number || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2"><strong>Primary Email:</strong> {selectedLab.contact_email || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2"><strong>Primary Phone:</strong> {selectedLab.contact_phone || '-'}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2"><strong>Status:</strong> {selectedLab.is_active ? 'Active' : 'Inactive'}</Typography></Grid>
              </Grid>
            </Paper>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Billing Contact</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Falls back to primary contact if not set</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}><Typography variant="body2"><strong>Name:</strong> {selectedLab.billing_contact_name || '-'}</Typography></Grid>
                <Grid item xs={4}><Typography variant="body2"><strong>Email:</strong> {selectedLab.billing_email || selectedLab.contact_email || '-'}</Typography></Grid>
                <Grid item xs={4}><Typography variant="body2"><strong>Phone:</strong> {selectedLab.billing_phone || selectedLab.contact_phone || '-'}</Typography></Grid>
              </Grid>
            </Paper>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Technical Contact</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Falls back to primary contact if not set</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}><Typography variant="body2"><strong>Name:</strong> {selectedLab.technical_contact_name || '-'}</Typography></Grid>
                <Grid item xs={4}><Typography variant="body2"><strong>Email:</strong> {selectedLab.technical_email || selectedLab.contact_email || '-'}</Typography></Grid>
                <Grid item xs={4}><Typography variant="body2"><strong>Phone:</strong> {selectedLab.technical_phone || selectedLab.contact_phone || '-'}</Typography></Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Add Integration Dialog */}
        <Dialog open={integrationDialogOpen} onClose={() => { setIntegrationDialogOpen(false); setDialogTestResult(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Add LIS Integration</DialogTitle>
          <DialogContent>
            <TextField label="LIS System Name" fullWidth margin="normal" placeholder="e.g. crelio, ligolab, orchard"
              value={integrationForm.lis_system} onChange={(e) => setIntegrationForm({ ...integrationForm, lis_system: e.target.value })} />
            <FormControl fullWidth margin="normal">
              <InputLabel>Integration Type</InputLabel>
              <Select value={integrationForm.integration_type} label="Integration Type"
                onChange={(e) => setIntegrationForm({ ...integrationForm, integration_type: e.target.value })}>
                <MenuItem value="sftp">SFTP</MenuItem>
                <MenuItem value="webhook">Webhook Callback</MenuItem>
                <MenuItem value="smtp">SMTP (Email)</MenuItem>
              </Select>
            </FormControl>
            <Divider sx={{ my: 2 }} />
            {integrationForm.integration_type === 'sftp' && (
              <>
                <TextField label="Host" fullWidth margin="dense" placeholder="sftp.example.com"
                  value={integrationForm.host} onChange={(e) => setIntegrationForm({ ...integrationForm, host: e.target.value })} />
                <TextField label="Port" fullWidth margin="dense" type="number"
                  value={integrationForm.port} onChange={(e) => setIntegrationForm({ ...integrationForm, port: e.target.value })} />
                <TextField label="Username" fullWidth margin="dense"
                  value={integrationForm.username} onChange={(e) => setIntegrationForm({ ...integrationForm, username: e.target.value })} />
                <TextField label="Password (optional)" fullWidth margin="dense" type="password"
                  value={integrationForm.password} onChange={(e) => setIntegrationForm({ ...integrationForm, password: e.target.value })} />
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1, gap: 1 }}>
                  <Button variant="outlined" component="label" disabled={keyFileUploading || !selectedLab}>
                    {keyFileUploading ? 'Uploading...' : 'Browse Key File'}
                    <input type="file" hidden accept=".pem,.key,.ppk,.pub"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !selectedLab) return;
                        setKeyFileUploading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const resp = await axios.post(
                            `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/upload-key`,
                            formData,
                            { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
                          );
                          if (resp.data.success) {
                            setIntegrationForm({ ...integrationForm, key_file: resp.data.file_path });
                            setKeyFileName(file.name);
                          } else {
                            setError(resp.data.error || 'Failed to upload key file');
                          }
                        } catch (err: any) {
                          setError(err.response?.data?.error || 'Failed to upload key file');
                        } finally {
                          setKeyFileUploading(false);
                        }
                      }}
                    />
                  </Button>
                  {keyFileName ? (
                    <Chip label={keyFileName} size="small" color="success" onDelete={() => {
                      setKeyFileName(null);
                      setIntegrationForm({ ...integrationForm, key_file: '' });
                    }} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No key file selected</Typography>
                  )}
                </Box>
                <TextField label="Remote Path In (ORU_OUT)" fullWidth margin="dense"
                  value={integrationForm.remote_path_in} onChange={(e) => setIntegrationForm({ ...integrationForm, remote_path_in: e.target.value })} />
                <TextField label="Remote Path Out (ORU_IN)" fullWidth margin="dense"
                  value={integrationForm.remote_path_out} onChange={(e) => setIntegrationForm({ ...integrationForm, remote_path_out: e.target.value })} />
              </>
            )}
            {integrationForm.integration_type === 'webhook' && (
              <>
                <Alert severity="info" sx={{ mb: 1 }}>
                  We will POST to your callback URL when a report is ready for this lab.
                </Alert>
                <TextField label="Callback URL" fullWidth margin="dense" placeholder="https://your-lis.com/api/webhooks/reasonshield"
                  helperText="We will POST report data to this URL when processing is complete"
                  value={integrationForm.callback_url} onChange={(e) => setIntegrationForm({ ...integrationForm, callback_url: e.target.value })} />
                <TextField label="Callback Secret (optional)" fullWidth margin="dense" type="password"
                  helperText="We'll sign the payload with HMAC-SHA256 using this secret so you can verify it's from us"
                  value={integrationForm.callback_secret} onChange={(e) => setIntegrationForm({ ...integrationForm, callback_secret: e.target.value })} />
                <TextField label="Events" fullWidth margin="dense"
                  helperText="Comma-separated: report_ready, report_failed, hl7_received"
                  value={integrationForm.callback_events} onChange={(e) => setIntegrationForm({ ...integrationForm, callback_events: e.target.value })} />
              </>
            )}
            {integrationForm.integration_type === 'smtp' && (
              <Alert severity="info">SMTP integration configuration coming soon.</Alert>
            )}

            {dialogTestResult && (
              <Alert severity={dialogTestResult.success ? 'success' : 'error'} sx={{ mt: 2 }}
                onClose={() => setDialogTestResult(null)}>
                {dialogTestResult.message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setIntegrationDialogOpen(false); setDialogTestResult(null); }}>Cancel</Button>
            <Button variant="outlined" color="secondary" onClick={handleTestBeforeSave}
              disabled={dialogTesting || integrationForm.integration_type === 'smtp' ||
                (integrationForm.integration_type === 'sftp' && !integrationForm.host) ||
                (integrationForm.integration_type === 'webhook' && !integrationForm.callback_url)}
              startIcon={dialogTesting ? <CircularProgress size={16} /> : <PlayArrowIcon />}>
              {dialogTesting ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button variant="contained" onClick={handleCreateIntegration}
              disabled={!integrationForm.lis_system}>
              Add Integration
            </Button>
          </DialogActions>
        </Dialog>

        {/* Lab Edit Dialog */}
        <Dialog open={labDialogOpen} onClose={() => { setLabDialogOpen(false); setEditingLab(false); }} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Lab</DialogTitle>
          <DialogContent>
            <TextField label="Lab ID" fullWidth margin="normal" disabled
              value={labForm.lab_id} />
            <TextField label="Lab Name" fullWidth margin="normal"
              value={labForm.name} onChange={(e) => setLabForm({ ...labForm, name: e.target.value })} />
            <TextField label="CLIA Number" fullWidth margin="normal"
              value={labForm.clia_number} onChange={(e) => setLabForm({ ...labForm, clia_number: e.target.value })} />
            <TextField label="Company Name" fullWidth margin="normal"
              value={labForm.company_name} onChange={(e) => setLabForm({ ...labForm, company_name: e.target.value })} />
            <TextField label="Primary Contact Email" fullWidth margin="normal"
              value={labForm.contact_email} onChange={(e) => setLabForm({ ...labForm, contact_email: e.target.value })} />
            <TextField label="Primary Contact Phone" fullWidth margin="normal"
              value={labForm.contact_phone} onChange={(e) => setLabForm({ ...labForm, contact_phone: e.target.value })} />
            <FormControl fullWidth margin="normal">
              <InputLabel>Testing Methodology</InputLabel>
              <Select value={labForm.testing_methodology} label="Testing Methodology"
                onChange={(e) => setLabForm({ ...labForm, testing_methodology: e.target.value })}>
                <MenuItem value="molecular">Molecular — PCR resistance-gene detection</MenuItem>
                <MenuItem value="phenotypic">Phenotypic — culture + susceptibility testing</MenuItem>
                <MenuItem value="mixed">Mixed — both, per panel</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                How this lab establishes resistance. A gene panel detects markers rather than testing drugs
                against an isolate, so the monthly report is worded differently for each.
              </Typography>
            </FormControl>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">Billing Contact (optional)</Typography>
            <TextField label="Billing Contact Name" fullWidth margin="dense"
              value={labForm.billing_contact_name} onChange={(e) => setLabForm({ ...labForm, billing_contact_name: e.target.value })} />
            <TextField label="Billing Email" fullWidth margin="dense"
              value={labForm.billing_email} onChange={(e) => setLabForm({ ...labForm, billing_email: e.target.value })} />
            <TextField label="Billing Phone" fullWidth margin="dense"
              value={labForm.billing_phone} onChange={(e) => setLabForm({ ...labForm, billing_phone: e.target.value })} />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">Technical Contact (optional)</Typography>
            <TextField label="Technical Contact Name" fullWidth margin="dense"
              value={labForm.technical_contact_name} onChange={(e) => setLabForm({ ...labForm, technical_contact_name: e.target.value })} />
            <TextField label="Technical Email" fullWidth margin="dense"
              value={labForm.technical_email} onChange={(e) => setLabForm({ ...labForm, technical_email: e.target.value })} />
            <TextField label="Technical Phone" fullWidth margin="dense"
              value={labForm.technical_phone} onChange={(e) => setLabForm({ ...labForm, technical_phone: e.target.value })} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setLabDialogOpen(false); setEditingLab(false); }}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateLab} disabled={!labForm.name}>
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── List View ────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">My Labs</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetLabForm(); setEditingLab(false); setLabDialogOpen(true); }}>
          Add Lab
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {labs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No labs yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Add your first lab to start sending results through ReasonShield.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetLabForm(); setEditingLab(false); setLabDialogOpen(true); }}>
            Add Your First Lab
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lab Name</TableCell>
                <TableCell>Lab ID</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="center">Integrations</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labs.map((lab) => (
                <TableRow key={lab.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => fetchLabDetail(lab.lab_id)}>
                  <TableCell><strong>{lab.name}</strong></TableCell>
                  <TableCell>{lab.lab_id}</TableCell>
                  <TableCell>{lab.company_name || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip label={lab.integration_count} size="small"
                      color={lab.integration_count > 0 ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={lab.is_active ? 'Active' : 'Inactive'} size="small"
                      color={lab.is_active ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Lab Dialog */}
      <Dialog open={labDialogOpen && !editingLab} onClose={() => setLabDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Lab</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Register a new laboratory to start integrating with ReasonShield.
          </Typography>
          <TextField label="Lab ID (CLIA Number)" fullWidth margin="normal" required
            placeholder="e.g. 1000942"
            value={labForm.lab_id} onChange={(e) => setLabForm({ ...labForm, lab_id: e.target.value })} />
          <TextField label="Lab Name" fullWidth margin="normal" required
            placeholder="e.g. Augustus Lab"
            value={labForm.name} onChange={(e) => setLabForm({ ...labForm, name: e.target.value })} />
          <TextField label="CLIA Number" fullWidth margin="normal"
            value={labForm.clia_number} onChange={(e) => setLabForm({ ...labForm, clia_number: e.target.value })} />
          <TextField label="Company Name" fullWidth margin="normal"
            value={labForm.company_name} onChange={(e) => setLabForm({ ...labForm, company_name: e.target.value })} />
          <TextField label="Contact Email" fullWidth margin="normal"
            value={labForm.contact_email} onChange={(e) => setLabForm({ ...labForm, contact_email: e.target.value })} />
          <TextField label="Contact Phone" fullWidth margin="normal"
            value={labForm.contact_phone} onChange={(e) => setLabForm({ ...labForm, contact_phone: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Testing Methodology</InputLabel>
            <Select value={labForm.testing_methodology} label="Testing Methodology"
              onChange={(e) => setLabForm({ ...labForm, testing_methodology: e.target.value })}>
              <MenuItem value="molecular">Molecular — PCR resistance-gene detection</MenuItem>
              <MenuItem value="phenotypic">Phenotypic — culture + susceptibility testing</MenuItem>
              <MenuItem value="mixed">Mixed — both, per panel</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              How this lab establishes resistance. A gene panel detects markers rather than testing drugs
              against an isolate, so the monthly report is worded differently for each.
            </Typography>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLab}
            disabled={!labForm.lab_id || !labForm.name}>
            Create Lab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabManager;
